import type { Rectangle2d } from "@logic/lib/2d"
import type { Phase } from "@logic/lib/design"
import type { RecoilState } from "recoil"
import { useRecoilTransaction_UNSTABLE } from "recoil"

import { designLocalState } from "~/containers/Proposal/state/designs/design-state"

import type { TransactionOperation } from ".."
import {
  gatherPointsWithinBox,
  gatherRectanglesWithinBox,
} from "./gatherWithinBox"
import { getDragBox } from "./getDragBox"
import { startDrag } from "./startDrag"
import { shiftIsDownState, startPointState } from "../../../inputs"
import { findNodeState, nodeIndex } from "../../nodes"
import { nodeSelectionState_INTERNAL } from "../../nodeSelection"
import { findPanelState, panelIndex, panelSelectionState } from "../../panels"

export type ElementGatherer = TransactionOperation<Rectangle2d, string[]>

type ElementGatheringTools = {
  gather: ElementGatherer
  selectionState: RecoilState<Set<string>>
}

// Built lazily: this module sits in a circular-import cluster with the
// element atoms, so referencing them at module scope can throw a TDZ
// ReferenceError depending on which module the bundler evaluates first.
const gatheringToolsForPhase = (): Partial<
  Record<Phase, ElementGatheringTools>
> => ({
  "Outline": {
    gather: (transactors, dragBox) =>
      gatherPointsWithinBox(transactors, {
        box: dragBox,
        pointStore: [findNodeState, nodeIndex],
      }),
    selectionState: nodeSelectionState_INTERNAL,
  },
  "Panel Placement": {
    gather: (transactors, dragBox) =>
      gatherRectanglesWithinBox(transactors, {
        box: dragBox,
        rectangleStore: [findPanelState, panelIndex],
      }),
    selectionState: panelSelectionState,
  },
})

export const finishDragSelect: TransactionOperation = (transactors) => {
  const { get, set } = transactors
  const dragBox = getDragBox(transactors)
  if (dragBox) {
    const { phase } = get(designLocalState)
    const gatheringTools = gatheringToolsForPhase()[phase]
    if (gatheringTools) {
      const { gather, selectionState } = gatheringTools
      const gathered = gather(transactors, dragBox)
      const shiftIsDown = get(shiftIsDownState)
      if (shiftIsDown) {
        const newSelection = get(selectionState)
        gathered.forEach((id) =>
          newSelection.has(id) ? newSelection.delete(id) : newSelection.add(id),
        )
        set(selectionState, newSelection)
      } else {
        set(selectionState, new Set(gathered))
      }
    }
  }
  set(startPointState, null)
}

export const useDragSelect: () => [VoidFunction, VoidFunction] = () => [
  useRecoilTransaction_UNSTABLE((transactors) => () => startDrag(transactors)),
  useRecoilTransaction_UNSTABLE(
    (transactors) => () => finishDragSelect(transactors),
  ),
]
